import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ShieldCheck, ShieldAlert, ScanLine, Search, UserPlus, LogOut as LogOutIcon,
  Loader2, CheckCircle2, XCircle, Clock, RefreshCw, Trash2,
} from 'lucide-react'
import { format, parseISO, isValid, formatDistanceToNowStrict } from 'date-fns'
import toast from 'react-hot-toast'
import { gate, checkins, members as membersApi } from '../api/endpoints.js'

const DENY_LABEL = {
  denied_inactive: 'Membership inactive',
  denied_no_member: 'No matching member',
  denied_anti_passback: 'Anti-passback (already inside)',
  denied_no_24hr: 'Outside 24-hour access',
  denied_suspended: 'Membership suspended',
  error: 'Scanner error',
  granted: 'Granted',
}

const when = (v) => {
  if (!v) return '—'
  const d = typeof v === 'string' ? parseISO(v) : new Date(v)
  return isValid(d) ? format(d, 'MMM d, h:mm a') : '—'
}
const ago = (v) => {
  if (!v) return ''
  const d = typeof v === 'string' ? parseISO(v) : new Date(v)
  return isValid(d) ? `${formatDistanceToNowStrict(d)} ago` : ''
}
const nameOf = (m) =>
  m?.full_name || `${m?.first_name ?? ''} ${m?.last_name ?? ''}`.trim() || m?.email || '—'

/* --------------------------------------------------------------- scanner -- */
function Scanner({ gateId }) {
  const qc = useQueryClient()
  const [token, setToken] = useState('')
  const [result, setResult] = useState(null)
  const inputRef = useRef(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const scan = useMutation({
    mutationFn: (t) => gate.scan(t, gateId),
    onSuccess: (r) => {
      const d = r.data
      setResult(d)
      if (d.access_granted) toast.success(`${d.member_name ?? 'Member'} — access granted`)
      else toast.error(DENY_LABEL[d.reason] ?? d.reason ?? 'Access denied')
      setToken('')
      qc.invalidateQueries({ queryKey: ['gate'] })
      qc.invalidateQueries({ queryKey: ['checkins'] })
      setTimeout(() => inputRef.current?.focus(), 50)
    },
    onError: (e) => {
      setResult({ access_granted: false, reason: e?.response?.data?.detail || 'error' })
      toast.error('Scan failed')
      setToken('')
    },
  })

  const granted = result?.access_granted
  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-3">
        <ScanLine className="w-4 h-4 text-primary" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-muted">
          Scan to enter
        </h2>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); if (token.trim()) scan.mutate(token.trim()) }}>
        <input
          ref={inputRef}
          className="input-field !text-lg !py-3 font-mono tracking-wide"
          placeholder="Scan a member QR code or type their token…"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          autoComplete="off"
          spellCheck="false"
        />
        <button type="submit" className="btn-primary w-full mt-3 inline-flex items-center justify-center gap-2"
                disabled={scan.isPending || !token.trim()}>
          {scan.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
          {scan.isPending ? 'Checking…' : 'Check access'}
        </button>
      </form>

      {result && (
        <div
          className="mt-4 rounded-xl p-4 flex items-start gap-3 animate-pop"
          style={{
            background: granted ? 'rgb(var(--ok-bg))' : 'rgb(var(--danger-bg))',
            border: `1px solid rgb(var(--${granted ? 'ok' : 'danger'}-border))`,
          }}
        >
          {granted
            ? <CheckCircle2 className="w-6 h-6 shrink-0" style={{ color: 'rgb(var(--ok))' }} />
            : <XCircle className="w-6 h-6 shrink-0" style={{ color: 'rgb(var(--danger))' }} />}
          <div className="min-w-0">
            <div className="font-bold" style={{ color: `rgb(var(--${granted ? 'ok' : 'danger'}))` }}>
              {granted ? 'Access granted' : 'Access denied'}
            </div>
            <div className="text-sm text-text-primary truncate">
              {result.member_name || DENY_LABEL[result.reason] || result.reason}
            </div>
            {granted && result.reason && result.reason !== 'granted' && (
              <div className="text-xs text-text-muted">{result.reason}</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/* --------------------------------------------------- manual staff check-in -- */
function ManualCheckIn({ gateId }) {
  const qc = useQueryClient()
  const [q, setQ] = useState('')

  const { data } = useQuery({
    queryKey: ['members', 'gate-search', q],
    queryFn: () => membersApi.list({ q, page: 1, per_page: 6 }).then((r) => r.data),
    enabled: q.length >= 2,
  })
  const results = Array.isArray(data) ? data : data?.items ?? []

  const go = useMutation({
    mutationFn: (memberId) => checkins.create({
      member_id: memberId, method: 'staff', type: 'entry', gate_id: gateId,
    }),
    onSuccess: () => {
      toast.success('Checked in')
      setQ('')
      qc.invalidateQueries({ queryKey: ['checkins'] })
      qc.invalidateQueries({ queryKey: ['gate'] })
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Check-in failed'),
  })

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-3">
        <UserPlus className="w-4 h-4 text-primary" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-muted">
          Manual check-in
        </h2>
      </div>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-dim pointer-events-none" />
        <input className="input-field !pl-8" placeholder="Search a member…"
               value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {q.length >= 2 && (
        <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
          {results.length === 0 ? (
            <p className="text-sm text-text-dim px-1 py-2">No matches</p>
          ) : results.map((m) => (
            <button key={m.id} disabled={go.isPending} onClick={() => go.mutate(m.id)}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-surface-3
                               transition-colors flex items-center justify-between gap-2 disabled:opacity-50">
              <span className="truncate">{nameOf(m)}</span>
              <span className="text-xs text-primary shrink-0">Check in</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------- page -- */
export default function GatePage() {
  const qc = useQueryClient()
  const [tab, setTab] = useState('recent')
  const [busyId, setBusyId] = useState(null)
  const gateId = 'front-desk'

  const { data: logs, isLoading: logsLoading } = useQuery({
    queryKey: ['gate', 'logs'],
    queryFn: () => gate.list({ limit: 50 }).then((r) => r.data),
    refetchInterval: 20_000,
  })
  const { data: denied, isLoading: deniedLoading } = useQuery({
    queryKey: ['gate', 'denied'],
    queryFn: () => gate.denied({ limit: 50 }).then((r) => r.data),
    refetchInterval: 20_000,
  })
  const { data: active, isLoading: activeLoading } = useQuery({
    queryKey: ['checkins', 'active'],
    queryFn: () => checkins.list({ page: 1, page_size: 50 }).then((r) => r.data),
    refetchInterval: 20_000,
  })

  const asList = (d) => (Array.isArray(d) ? d : d?.items ?? [])
  const inside = asList(active).filter((c) => !c.checked_out_at)

  const removeCheckin = useMutation({
    mutationFn: (id) => checkins.remove(id),
    onMutate: (id) => setBusyId(id),
    onSuccess: () => {
      toast.success('Check-in removed')
      qc.invalidateQueries({ queryKey: ['checkins'] })
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not remove check-in'),
    onSettled: () => setBusyId(null),
  })

  const checkout = useMutation({
    mutationFn: (id) => checkins.checkout(id),
    onMutate: (id) => setBusyId(id),
    onSuccess: () => {
      toast.success('Checked out')
      qc.invalidateQueries({ queryKey: ['checkins'] })
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Checkout failed'),
    onSettled: () => setBusyId(null),
  })

  const rows = tab === 'recent' ? asList(logs) : tab === 'denied' ? asList(denied) : inside
  const loading = tab === 'recent' ? logsLoading : tab === 'denied' ? deniedLoading : activeLoading

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="page-title">Gate</h1>
        <button className="btn-secondary inline-flex items-center gap-2"
                onClick={() => qc.invalidateQueries()}>
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Scanner gateId={gateId} />
        <div className="space-y-4">
          <ManualCheckIn gateId={gateId} />
          <div className="grid grid-cols-2 gap-4">
            <div className="stat-card">
              <p className="text-xs uppercase tracking-wider text-text-muted font-medium">Inside now</p>
              <p className="text-2xl font-bold mt-0.5 tabular-nums">{inside.length}</p>
            </div>
            <div className="stat-card">
              <p className="text-xs uppercase tracking-wider text-text-muted font-medium">Denied today</p>
              <p className="text-2xl font-bold mt-0.5 tabular-nums"
                 style={{ color: asList(denied).length ? 'rgb(var(--danger))' : undefined }}>
                {asList(denied).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {[['recent', 'Recent access', ShieldCheck],
          ['denied', 'Denied', ShieldAlert],
          ['inside', 'Inside now', Clock]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)}
                  className={`tab-btn inline-flex items-center gap-2 ${tab === k ? 'tab-btn-active' : 'tab-btn-inactive'}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      <div className="table-container">
        <table className="table-base">
          <thead className="table-head">
            <tr>
              <th>Member</th>
              <th>{tab === 'inside' ? 'Checked in' : 'Scanned'}</th>
              <th>{tab === 'inside' ? 'Method' : 'Result'}</th>
              <th>Gate</th>
              {tab === 'inside' && <th className="text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>{Array.from({ length: tab === 'inside' ? 5 : 4 }).map((_, j) => (
                  <td key={j} className="px-4 py-3"><div className="skeleton h-4 w-3/4" /></td>))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-text-dim">
                  <ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  {tab === 'denied' ? 'No denied scans.' : tab === 'inside' ? 'Nobody is checked in.' : 'No scans yet.'}
                </td>
              </tr>
            ) : rows.map((r, i) => (
              <tr key={r.id ?? i} className="hover:bg-surface-3/60 transition-colors">
                <td className="px-4 py-3 text-sm text-text-primary">{nameOf(r.member)}</td>
                <td className="px-4 py-3 text-sm text-text-muted">
                  {when(r.scanned_at ?? r.checked_in_at)}
                  <span className="text-text-dim ml-1 text-xs">{ago(r.scanned_at ?? r.checked_in_at)}</span>
                </td>
                <td className="px-4 py-3">
                  {tab === 'inside' ? (
                    <span className="badge-info">{(r.method ?? '').replace('_', ' ')}</span>
                  ) : (
                    <span className={r.result === 'granted' ? 'badge-active' : 'badge-suspended'}>
                      {DENY_LABEL[r.result] ?? r.result ?? '—'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-text-dim">{r.gate_id ?? '—'}</td>
                {tab === 'inside' && (
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <button title="Check out" aria-label="Check out" disabled={busyId === r.id}
                              onClick={() => checkout.mutate(r.id)}
                              className="p-1.5 rounded-md text-text-dim hover:text-primary hover:bg-primary/12 transition-all active:scale-95 disabled:opacity-40">
                        {busyId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOutIcon className="w-4 h-4" />}
                      </button>
                      <button title="Delete check-in" aria-label="Delete check-in" disabled={busyId === r.id}
                              onClick={() => { if (confirm('Delete this check-in record?')) removeCheckin.mutate(r.id) }}
                              className="p-1.5 rounded-md text-text-dim hover:text-[rgb(var(--danger))] hover:bg-[rgb(var(--danger))]/12 transition-all active:scale-95 disabled:opacity-40">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
